import { useState, type ReactNode } from 'react';
import { Search } from 'lucide-react';
import type { ActualsResponse, KpiCatalogueEntry, OpenEvidence } from '../../types/strategicKpis';
import { CATEGORY_LABEL } from '../../utils/strategicKpiFormat';
import { SKPI_FAMILIES, type MatrixCell, type PeopleGroup, type PeopleView, type SkpiFamily } from '../../utils/strategicKpiUrl';
import { matrixFamilies, resolveFamily } from './DrillDownTable';
import { Leaderboard } from './Leaderboard';
import { useBodyTableOffset } from './useTableOffset';

export const PEOPLE_CELL_LABEL: Record<MatrixCell, string> = {
    achievement: 'Achieved',
    actual: 'Actual',
    target: 'Target',
    prior: 'Prior year',
};
const CELL_ORDER: readonly MatrixCell[] = ['achievement', 'actual', 'target', 'prior'];

/** The families the matrix segment offers (E18): those with a KPI in the payload (`data.kpis`), BAB included, in
 *  catalogue order - `matrixFamilies` of DrillDownTable, so a family without a steered KPI (2026: Gross Profit,
 *  BAB) is still offered; all of them when the list is empty. */
export function peopleFamilies(kpis: Pick<KpiCatalogueEntry, 'category'>[]): SkpiFamily[] {
    const present = matrixFamilies(kpis);
    return present.length > 0 ? present : [...SKPI_FAMILIES];
}

/** The family the matrix shows when the address names none (or one not offered): the first offered
 *  (`resolveFamily`, the one DrillDownTable draws). */
export function peopleFamily(kpis: Pick<KpiCatalogueEntry, 'category'>[], fam: SkpiFamily | null): SkpiFamily {
    return resolveFamily(kpis, fam) ?? peopleFamilies(kpis)[0];
}

interface Props {
    data: ActualsResponse;
    /** Dims the content while a new response is computed. */
    loading?: boolean;
    /** `steeredKpis(data)`. */
    steered: KpiCatalogueEntry[];
    view: PeopleView;
    onView: (v: PeopleView) => void;
    /** Ranking: the KPI (null = default) and the grouping. */
    kpi: string | null;
    onKpi: (kpiKey: string) => void;
    group: PeopleGroup;
    onGroup: (g: PeopleGroup) => void;
    /**
     * Matrix: the one family (null = `peopleFamily` default) and the cell mode, as segments in this toolbar.
     * Leave a callback out when the matrix draws that segment itself (DrillDownTable with onFamChange /
     * onCellModeChange, B3b) — then this toolbar does not, so it never appears twice.
     */
    fam?: SkpiFamily | null;
    onFam?: (f: SkpiFamily) => void;
    cell?: MatrixCell;
    onCell?: (c: MatrixCell) => void;
    /** The matrix itself (DrillDownTable, wired by B4), or a function of the search text. The matrix has no
     *  search field (the search belongs to the ranking), so the function is called with ''. */
    matrix: ReactNode | ((search: string) => ReactNode);
    /** Further matrix controls after the cell segment (hierarchy, «hide persons without targets»). */
    matrixControls?: ReactNode;
    onOpenEvidence: OpenEvidence;
    /** A ranking row opens the person panel (PersonPanel in the page's SidePanel). */
    onOpenPerson?: (staffId: string) => void;
    /** The person whose panel is open. */
    selectedStaffId?: string | null;
}

/**
 * People (masterplan skpi-redesign §4, package B3): one toolbar — the view segment Ranking | Matrix, the
 * controls of the view, the person search — and one table at the end of the tab (§2 rule 5).
 *
 * Ranking: Leaderboard (one KPI, grouped by none/team/company, sorted by achievement). Matrix: whatever the
 * page passes as `matrix` (DrillDownTable, one family per E18). The state — view, KPI, group, family, cell —
 * comes in as props and leaves through callbacks: the page keeps it in the URL (B4) and across tab changes
 * (E22). Only the search text is this tab's own.
 */
export function PeopleTab({
    data, loading = false, steered, view, onView, kpi, onKpi, group, onGroup, fam = null, onFam, cell = 'achievement', onCell,
    matrix, matrixControls, onOpenEvidence, onOpenPerson, selectedStaffId = null,
}: Props) {
    const [search, setSearch] = useState('');
    const { body, style } = useBodyTableOffset([view, data, kpi, group, fam, cell]);

    const viewSeg = (
        <div className="seg" role="group" aria-label="View" data-testid="skpi-people-view">
            <button type="button" onClick={() => onView('ranking')} aria-pressed={view === 'ranking'} title="One KPI, every person with a target for it, most behind first">Ranking</button>
            <button type="button" onClick={() => onView('matrix')} aria-pressed={view === 'matrix'} title="BU › Company › Team › Person, one family of KPIs">Matrix</button>
        </div>
    );
    const searchBox = (
        <div className="search">
            <Search className="i sm" aria-hidden="true" />
            <input
                type="search"
                className="input"
                placeholder="Find a person"
                aria-label="Find a person"
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="skpi-people-search"
            />
        </div>
    );

    const families = peopleFamilies(data.kpis);
    const activeFam = peopleFamily(data.kpis, fam);

    return (
        <section className={`space-y-4${loading ? ' opacity-60' : ''}`} aria-busy={loading || undefined} data-testid="skpi-people">
            <div ref={body} style={style}>
                {view === 'ranking' ? (
                    <Leaderboard
                        data={data}
                        steered={steered}
                        onOpenEvidence={onOpenEvidence}
                        kpiKey={kpi}
                        onKpiKey={onKpi}
                        groupBy={group}
                        onGroupBy={onGroup}
                        search={search}
                        onOpenPerson={onOpenPerson}
                        selectedStaffId={selectedStaffId}
                        toolbarStart={viewSeg}
                        toolbarEnd={searchBox}
                    />
                ) : (
                    <div className="space-y-4">
                        <div className="toolbar">
                            {viewSeg}
                            {onFam && (
                                <div className="seg" role="group" aria-label="Family" data-testid="skpi-people-family">
                                    {families.map(f => (
                                        <button key={f} type="button" onClick={() => onFam(f)} aria-pressed={activeFam === f}>{CATEGORY_LABEL[f] ?? f}</button>
                                    ))}
                                </div>
                            )}
                            {onCell && (
                                <>
                                    <span className="lbl" id="skpi-people-cell-label">Cell</span>
                                    <div className="seg" role="group" aria-labelledby="skpi-people-cell-label" data-testid="skpi-people-cell">
                                        {CELL_ORDER.map(c => (
                                            <button key={c} type="button" onClick={() => onCell(c)} aria-pressed={cell === c}>{PEOPLE_CELL_LABEL[c]}</button>
                                        ))}
                                    </div>
                                </>
                            )}
                            {matrixControls}
                        </div>
                        {typeof matrix === 'function' ? matrix('') : matrix}
                    </div>
                )}
            </div>
        </section>
    );
}
